import React, { useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import { useNavigate, useParams } from 'react-router-dom'
import ComponentMapper from '@renderer/components/mapper/ComponentMapper'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { Package, Component } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { unifiedCameraUtils } from '@renderer/utils'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { BackgroundContainer } from '@renderer/components/BackgroundContainer'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import ScaleToFit from '@renderer/components/ScaleToFit'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import type { DesignOrientation } from '@renderer/types/configState'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'
import { resolveWelcomeDesign } from '@renderer/utils/welcomeDesign'
import { isDigitalMode } from '@renderer/lib/runtime'
import { ensureCameraReadyForStart } from '@renderer/utils/cameras/cameraStartGate'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'
import { useHubAutoStart, useHubCameraSlot } from '@renderer/hooks/useHubAutoStart'
import { resolveStartDestination } from '@renderer/utils/navigationUtils'
import { MULTI_PROJECT_HUB_FLAG } from '@renderer/utils/hubSupport'
import Loading from '@renderer/components/Loading'
import { isRecordingPackageType, toRecordingType } from '@renderer/utils/recordingType'
import InterruptedPaidSessionPrompt from './InterruptedPaidSessionPrompt'

const WelcomePage: React.FC = () => {
  const { setSessionState, logActivity } = useSessionStateStore()
  const { projectId, hubId } = useParams()
  const navigate = useNavigate()
  const { config, getPageSettings } = useConfigStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  const welcomeSettings = getPageSettings('welcome')
  const [isSessionStarting, setIsSessionStarting] = useState(false)
  const hubFlag = useFeatureFlag(MULTI_PROJECT_HUB_FLAG)
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()
  const designOrientation: DesignOrientation = deviceIsPortrait ? 'portrait' : 'landscape'

  // The design may come from the other orientation (see resolveWelcomeDesign);
  // size the canvas to the orientation it was laid out on and let ScaleToFit letterbox.
  const welcomeDesign = resolveWelcomeDesign(config?.pages?.welcome, designOrientation)
  const componentsForWelcome = welcomeDesign.components
  const screenSizeForWelcome = getDesignCanvasSizeForOrientation(
    config,
    welcomeDesign.orientation
  )

  const welcomeW = screenSizeForWelcome.width
  const welcomeH = screenSizeForWelcome.height
  const welcomeShort = Math.min(welcomeW, welcomeH)
  const isCompact = welcomeShort < 768
  const isPortraitLayout = welcomeH > welcomeW

  const startSession = async (mode: 'capture' | 'record'): Promise<void> => {
    if (isSessionStarting) {
      return // Prevent multiple calls
    }
    setIsSessionStarting(true)

    try {
      // Hub Welcome: the guest picks a mode BEFORE any session exists (plan D2).
      // Camera gate, credits and the session id belong to the member's start.
      // Gated on the HUB route param: a Project that still carries a legacy
      // `pages.modeSelect` (one that predates the hubs collection) must start
      // its own session, not hand off into a hub id that `/api/hubs` never had.
      if (hubId && resolveStartDestination(config, hubFlag) === 'mode-select') {
        navigate(`/hub/${hubId}/mode-select`)
        return
      }

      // Canonical project id for the session store: the loaded config's _id
      // (always a Mongo ObjectId). In digital mode the route param is a *slug*
      // (e.g. `panoramic`); persisting that would send a slug to POST
      // /api/sessions (→ Cast to ObjectId 500). config is loaded by ProjectRoute
      // before Welcome renders, so its _id is the resolved id. Kiosk: config._id
      // === projectId, so this is a no-op.
      const configId = useConfigStore.getState().config?._id
      const resolvedProjectId =
        typeof configId === 'string' && /^[a-f0-9]{24}$/i.test(configId)
          ? configId
          : projectId

      // Validate camera connection before starting session
      const bypassIds = [
        '67e3f8d12bb1bd3dbad28011',
        '67e3f8e32bb1bd3dbad28012',
        '67e3f8f02bb1bd3dbad28013'
      ]
      const isBypass = bypassIds.includes(projectId || '')

      // For capture mode, require camera connection (unless it's a bypass project).
      // Shared gate (utils/cameras/cameraStartGate) — the same check every brand
      // Welcome now runs, so kiosk and bahela/newspaper behave identically.
      if (mode === 'capture') {
        if (!(await ensureCameraReadyForStart())) {
          setIsSessionStarting(false)
          return
        }
      }

      // Skip credit validation for bypass projects
      if (isBypass) {
        const sessionId = takeSessionId()
        // Normalised, not the raw package type: a `'recording'` package used to be stored
        // as `'recording'`, which Capture's recorder gate does not accept — so unless
        // Checkout later overwrote it, no per-shot recorder was ever built.
        const recordingType = toRecordingType(
          config?.pages?.checkout?.settings?.packages?.find((pkg: Package) =>
            isRecordingPackageType(pkg.type)
          )?.type
        )

        // Set session ID FIRST before any logs are created
        setSessionState({
          id: sessionId,
          projectId,
          mode,
          timeline: {
            startTime: new Date()
          },
          userPreferences: {
            useGIF: getPageSettings('capture')?.gifEnabled,
            useRecording: getPageSettings('capture')?.recordingEnabled,
            useVideo: getPageSettings('record')?.enabled,
            recordingType
          },
          galleryKey: `${9999999999999 - Date.now()}-${sessionId}`
        })

        // Booth telemetry: track this session for the session_ended outcome event.
        if (projectId) useBoothEventStore.getState().setProjectContext(projectId)
        useBoothEventStore.getState().startBoothSession(sessionId)

        // Now log activity with the session ID set
        logActivity(`Session started in ${mode} mode.`)

        const filterSettings = getPageSettings('filter')
        const applyAfterCapture = filterSettings?.applyAfterCapture || false
        const captureMode = config?.pages.capture?.settings?.mode || 'standard'
        const frameEnabled = config?.pages.frame?.settings?.enabled || false
        
        let nextPage: string
        if (welcomeSettings?.startWithPayment) {
          nextPage = 'checkout'
        } else {
          if (applyAfterCapture) {
            // Filter appears after capture - go to Frame (if frame-based) or Capture (if standard)
            if (captureMode === 'frame-based' && frameEnabled) {
              nextPage = 'frame'
            } else {
              nextPage = 'capture'
            }
          } else {
            // Filter appears before capture - always go to Filter first
            nextPage = 'filter'
          }
        }
        if (selectedSecondaryCamera && !hubCameraSlot) {
          nextPage = 'camera'
        }

        // Delay session upload to allow more logs to be collected (pass sessionId so overlap is safe)
        setTimeout(() => {
          stateUploader.uploadSessionState(sessionId)
        }, 5000)

        navigate(`/${projectId}/${nextPage}`)
        return
      }
      // Credit deduction is now handled in Capture.tsx when capture session starts
      // This ensures customers only pay when they actually start capturing.
      //
      // P1-1: pre-flight entitlement check using *cached* credits +
      // subscription state — synchronous, no spinner, no network round-trip.
      // We fire-and-forget a background refresh so View.tsx's reactive
      // effect on (isSubscriptionValid, credits) can still surface
      // NoCreditsModal if the server later reveals the cache was stale.
      // The check exists purely to spare an obviously-out-of-credits
      // operator from walking the customer through the whole flow only
      // to hit a 403 at upload time.
      useCreditsStore
        .getState()
        .fetchSubscriptionAndCredits()
        .catch((preflightErr) => {
          console.warn('[WELCOME] Background subscription/credits refresh failed', preflightErr)
        })
      const cachedCredits = useCreditsStore.getState().credits
      const cachedSubscriptionValid = useSubscriptionStore.getState().isValid
      const hasUsableEntitlement =
        cachedSubscriptionValid ||
        cachedCredits === 'Unlimited' ||
        (typeof cachedCredits === 'number' && cachedCredits > 0)
      // Digital web booth is free for the consumer — the session runs on the
      // project owner's account and the server skips the credit debit for
      // public digital projects (see studio POST /api/sessions). So the
      // pre-flight entitlement gate must not block a digital visitor (who is
      // typically signed-out → /api/credits 401 → credits:0). Kiosk unchanged.
      if (!isDigitalMode() && !hasUsableEntitlement) {
        console.warn('[WELCOME] Pre-flight check blocked session start', {
          credits: cachedCredits,
          subscriptionValid: cachedSubscriptionValid
        })
        cogoToast.error(
          'No credits available. Please contact the operator to top up or activate a subscription.',
          { hideAfter: 6 }
        )
        return
      }

      const sessionId = takeSessionId()

      // Get recording package to determine type
      // Normalised, not the raw package type: a `'recording'` package used to be stored
      // as `'recording'`, which Capture's recorder gate does not accept — so unless
      // Checkout later overwrote it, no per-shot recorder was ever built.
      const recordingType = toRecordingType(
        config?.pages?.checkout?.settings?.packages?.find((pkg: Package) =>
          isRecordingPackageType(pkg.type)
        )?.type
      )

      // Set session ID FIRST before any logs are created
      setSessionState({
        id: sessionId,
        projectId: resolvedProjectId,
        mode,
        timeline: {
          startTime: new Date()
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled,
          recordingType
        },
        gallery: {
          id: `${9999999999999 - Date.now()}-${sessionId}`
        }
      })

      // Booth telemetry: track this session for the session_ended outcome event.
      if (projectId) useBoothEventStore.getState().setProjectContext(projectId)
      useBoothEventStore.getState().startBoothSession(sessionId)

      // Now log activity with the session ID set
      logActivity(`Session started in ${mode} mode.`)

      const frameEnabled = config?.pages.frame?.settings?.enabled || false
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string
      
      if (welcomeSettings?.startWithPayment) {
        // If startWithPayment is enabled, always go to checkout first
        nextPage = 'checkout'
      } else {
        if (applyAfterCapture) {
          if (frameEnabled) {
            nextPage = 'frame'
          } else {
            nextPage = 'capture'
          }
        } else {
          nextPage = 'filter'
        }
      }

      // Check for secondary camera - always go to camera if secondary camera exists,
      // unless the hub already assigned this mode a camera.
      if (
        selectedSecondaryCamera &&
        !hubCameraSlot &&
        (await unifiedCameraUtils.isDeviceResponding('secondary'))
      ) {
        nextPage = 'camera'
      }

      // Upload session state after a delay so we have more than "session started" (logs, etc.)
      // Avoids uploading an empty/minimal session that can appear as duplicate or incomplete
      setTimeout(() => {
        stateUploader.uploadSessionState(sessionId)
      }, 5000)

      navigate(`/${projectId}/${nextPage}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Failed to check credits. Please try again.')
    } finally {
      setIsSessionStarting(false)
    }
  }

  // Member Welcome entered from a hub: the guest already tapped Start and
  // picked a mode, so start on mount instead of showing a second Welcome.
  const hubAutoStart = useHubAutoStart(() => startSession('capture'))
  // Assigned by the hub: the operator already picked, so the guest is not asked.
  const hubCameraSlot = useHubCameraSlot()

  // Handle button actions from ComponentMapper
  const handleButtonAction = async (component: Component): Promise<void> => {
    if (!component.buttonAction) return
    const captureMode = config?.pages.capture?.settings?.mode || 'standard'
    const frameEnabled = config?.pages.frame?.settings?.enabled || false
    const filterSettings = getPageSettings('filter')
    const applyAfterCapture = filterSettings?.applyAfterCapture || false

    let nextPage: string
    
    if (welcomeSettings?.startWithPayment) {
      // If startWithPayment is enabled, always go to checkout first
      nextPage = 'checkout'
    } else {
      // If applyAfterCapture is false, Filter appears BEFORE capture - go to Filter
      // If applyAfterCapture is true, Filter appears AFTER capture - go to Frame/Capture
      if (applyAfterCapture) {
        // Filter appears after capture - go to Frame (if frame-based) or Capture (if standard)
        if (captureMode === 'frame-based' && frameEnabled) {
          nextPage = 'frame'
        } else {
          // Standard mode: go directly to capture (skip filter before capture)
          nextPage = 'capture'
        }
      } else {
        // Filter appears before capture - always go to Filter first
        nextPage = 'filter'
      }
    }

    // Check for secondary camera - always go to camera if secondary camera exists,
    // unless the hub already assigned this mode a camera.
    if (
      selectedSecondaryCamera &&
      !hubCameraSlot &&
      (await unifiedCameraUtils.isDeviceResponding('secondary'))
    ) {
      nextPage = 'camera'
    }
    logActivity(`User clicked on "${component.text}" button`, {
      to: nextPage
    })
    switch (component.buttonAction) {
      case 'capture':
        startSession('capture')
        break
      case 'record':
        // Record step retired. Legacy welcome designs still carry 'record'
        // buttons — start a normal capture session instead of dead-ending.
        startSession('capture')
        break
      default:
        console.warn('Unknown button action:', component.buttonAction)
    }
  }

  /**
   * Attended booths (and button-less Welcome designs, e.g. a full-bleed live
   * preview) start from a tap anywhere on the screen rather than from a button
   * component. Same entry point as a `capture` button — startSession owns the
   * routing, so there is no second copy of the next-page logic here.
   */
  const handleSurfaceTap = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!welcomeSettings?.tapAnywhereToStart || isSessionStarting) return
    // A button component owns its own action and its own log line; a tap that
    // lands on one must not also register as a surface tap.
    if ((event.target as HTMLElement).closest('[data-button-action]')) return
    logActivity('User tapped the welcome screen to start')
    startSession('capture')
  }

  if (hubAutoStart.pending) {
    return <Loading />
  }

  const welcomeBg = config?.theme?.backgroundColor || '#F8FAFC'

  const welcomeBackdrop = (
    <div className="h-full w-full" aria-hidden>
      <BackgroundContainer className="relative h-full w-full overflow-hidden">
        <ComponentMapper
          components={componentsForWelcome}
          designLayout={{ isCompact, isPortrait: isPortraitLayout }}
          variant="backdrop"
        />
      </BackgroundContainer>
    </div>
  )

  return (
    <div
      className="flex h-full min-h-0 w-full flex-1 flex-col"
      onClick={handleSurfaceTap}
      data-tap-anywhere={welcomeSettings?.tapAnywhereToStart ? 'true' : undefined}
    >
      <InterruptedPaidSessionPrompt projectId={projectId} />
      <ScaleToFit
        screenSize={screenSizeForWelcome}
        backgroundColor={welcomeBg}
        backdropChildren={welcomeBackdrop}
        blurBackgroundColor={welcomeBg}
      >
        <BackgroundContainer className="relative overflow-hidden" data-page="welcome">
          <ComponentMapper
            components={componentsForWelcome}
            onButtonAction={handleButtonAction}
            designLayout={{ isCompact, isPortrait: isPortraitLayout }}
          />
        </BackgroundContainer>
      </ScaleToFit>
    </div>
  )
}

export default WelcomePage
